/**
 * Email drafts API (WP11b/WP28 / Spec §K).
 */

import {
  emailDraftListResponseSchema,
  emailDraftSchema,
  patchEmailDraftInputSchema,
  type DraftStatus,
  type EmailDraft,
  type EmailDraftListItem,
  type EmailDraftListResponse,
  type PatchEmailDraftInput,
} from "@carago/shared";
import { z } from "zod";
import { apiFetch, type ApiFetchOptions } from "./api.js";

const approveResultSchema = emailDraftSchema
  .extend({
    hotel_name: z.string(),
    sent: z.literal(false),
  })
  .passthrough();

const rejectResultSchema = emailDraftSchema
  .extend({
    hotel_name: z.string(),
  })
  .passthrough();

export type ListDraftsParams = {
  status?: DraftStatus | null;
  cursor?: string | null;
  limit?: number;
};

function withQuery(
  path: string,
  params: Record<string, string | number | undefined | null>,
): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    qs.set(key, String(value));
  }
  const query = qs.toString();
  return query ? `${path}?${query}` : path;
}

export async function listDrafts(
  params: ListDraftsParams = {},
  options?: ApiFetchOptions,
): Promise<EmailDraftListResponse> {
  const raw = await apiFetch<unknown>(
    withQuery("/v1/drafts", {
      status: params.status,
      cursor: params.cursor,
      limit: params.limit,
    }),
    { method: "GET", ...options },
  );
  return emailDraftListResponseSchema.parse(raw);
}

export async function patchDraft(
  id: string,
  input: PatchEmailDraftInput,
  options?: ApiFetchOptions,
): Promise<EmailDraft> {
  const body = patchEmailDraftInputSchema.parse(input);
  const raw = await apiFetch<unknown>(`/v1/drafts/${id}`, {
    method: "PATCH",
    body,
    ...options,
  });
  return emailDraftSchema.parse(raw);
}

export async function approveDraft(
  id: string,
  options?: ApiFetchOptions,
): Promise<EmailDraftListItem & { sent: false }> {
  const raw = await apiFetch<unknown>(`/v1/drafts/${id}/approve`, {
    method: "POST",
    ...options,
  });
  return approveResultSchema.parse(raw) as EmailDraftListItem & {
    sent: false;
  };
}

export async function rejectDraft(
  id: string,
  reason?: string,
  options?: ApiFetchOptions,
): Promise<EmailDraftListItem> {
  const raw = await apiFetch<unknown>(`/v1/drafts/${id}/reject`, {
    method: "POST",
    body: reason != null ? { reason } : {},
    ...options,
  });
  return rejectResultSchema.parse(raw) as EmailDraftListItem;
}
